import type { ReactNode } from 'react';
import { MailFrame } from '@/components/MailFrame';
import RichBodyEditor from '@/components/RichBodyEditor';
import { Label } from '@/components/ui/label';

/**
 * A message being written, drawn as the letter it will arrive as.
 *
 * Both of the composer's steps write one of these: the batch's letter on the
 * first, and then one per vendor on the previews. They were two different
 * screens' worth of controls — a boxed subject input and a bordered editor on
 * one, an envelope on the other — which made the previews look like a form for
 * editing the thing the planner had just written rather than the thing itself.
 * One component, so the two cannot drift again.
 *
 * What varies between the two is passed in: the previews have a recipient
 * already (it is the card's heading) while the batch's letter has a live "To"
 * line, which arrives as `headerRows` under the subject.
 */
export default function MessageSheet({
  idPrefix,
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  bodyLabel,
  resolveVariable,
  insertAsValue,
  signatureHtml,
  headerRows,
  minHeight,
}: {
  /** Namespaces the field ids, which have to be unique across a page that
   *  renders one of these per vendor. */
  idPrefix: string;
  subject: string;
  onSubjectChange: (subject: string) => void;
  /** Sanitized html, as `RichBodyEditor` speaks it. */
  body: string;
  onBodyChange: (html: string) => void;
  /** What the body is called to a screen reader — "Message", or the vendor it
   *  is addressed to when there are several on the page. */
  bodyLabel: string;
  /** What this page already knows about a merge variable, for the chips to
   *  show in place of their names. See `RichBodyEditor`. */
  resolveVariable?: (name: string) => string | undefined;
  /** Whether the palette drops in the answer as prose rather than a chip —
   *  true once this letter is one vendor's rather than the batch's. See
   *  `RichBodyEditor`. */
  insertAsValue?: boolean;
  signatureHtml?: string | null;
  /** Extra envelope rows under the subject — the live "To" line, on the step
   *  that still has one to fill. */
  headerRows?: ReactNode;
  minHeight?: string;
}) {
  const signature = signatureHtml?.trim();

  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--color-outline)]">
      {/* The envelope, as a mail client shows it. Without the subject above
          the body, the field most likely to embarrass a planner is the one the
          composer never renders. */}
      <div className="border-b border-border">
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1 px-4 pt-4 pb-3">
          {/* Both envelope labels are one fixed column wide, so the subject
              and anything under it start on the same vertical: a letter whose
              header fields begin at different x reads as two controls that
              happened to be stacked. Quieter than the composer's other labels
              on purpose — this is letterhead, and the subject line it names
              keeps precedence. */}
          <Label
            htmlFor={`${idPrefix}-subject`}
            className="mt-1 w-14 shrink-0 text-xs font-normal text-muted-foreground"
          >
            Subject
          </Label>
          <div className="min-w-0 flex-1 basis-40">
            {/* The same editor as the body, in one-line text mode — not the
                app's variable input. That one paints its chips in a mirror
                layered over a transparent field, glyph for glyph, so a chip
                cannot show anything but the exact token text without the caret
                landing somewhere the planner did not click. The subject has to
                fill in the way the body does, and one letter cannot answer
                that question two ways. */}
            <RichBodyEditor
              id={`${idPrefix}-subject`}
              ariaLabel="Subject"
              variant="envelope"
              toolbar="none"
              variables={false}
              singleLine
              output="text"
              minHeight="min-h-0"
              className="font-medium"
              value={subject}
              resolveVariable={resolveVariable}
              onChange={onSubjectChange}
            />
          </div>
        </div>

        {headerRows}
      </div>

      <RichBodyEditor
        id={`${idPrefix}-body`}
        ariaLabel={bodyLabel}
        variant="envelope"
        toolbar="bottom"
        minHeight={minHeight ?? 'min-h-[14rem]'}
        value={body}
        resolveVariable={resolveVariable}
        insertAsValue={insertAsValue}
        onChange={onBodyChange}
      >
        {/* The signature, at the foot of the letter where the server will put
            it — not as a labelled notice that there is going to be one. A
            frame rather than text in the editor because the server owns it: it
            arrives as its own block with inline images, and anything typed
            over a copy of it here would be a second signature on the sent
            mail. `padding` matches the body's own gutter, so it starts on the
            same left edge as the sentence above it. */}
        {signature && (
          <MailFrame
            html={signature}
            title="Your signature"
            padding={16}
            className="rounded-none border-0"
          />
        )}
      </RichBodyEditor>
    </div>
  );
}
